import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';
import VoiceInput from '../components/VoiceInput';

const STEPS = [
  { key: 'visit', title: 'Visit Info', icon: 'fa-clipboard-check' },
  { key: 'status', title: 'Overall Status', icon: 'fa-heart-pulse' },
  { key: 'vitals', title: 'Vitals', icon: 'fa-stethoscope' },
  { key: 'mental', title: 'Mental & Cognitive', icon: 'fa-brain' },
  { key: 'mood', title: 'Mood & Behavior', icon: 'fa-face-smile' },
  { key: 'pain', title: 'Pain', icon: 'fa-hand-dots' },
  { key: 'meals', title: 'Meals', icon: 'fa-utensils' },
  { key: 'fluids', title: 'Fluid Intake', icon: 'fa-glass-water' },
  { key: 'meds', title: 'Medication', icon: 'fa-pills' },
  { key: 'mobility', title: 'Mobility', icon: 'fa-person-walking' },
  { key: 'adls', title: 'ADLs', icon: 'fa-shower' },
  { key: 'toileting', title: 'Toileting', icon: 'fa-toilet' },
  { key: 'skin', title: 'Skin & Body', icon: 'fa-hand' },
  { key: 'sleep', title: 'Sleep', icon: 'fa-moon' },
  { key: 'activities', title: 'Activities', icon: 'fa-person-hiking' },
  { key: 'fall', title: 'Fall / Incident', icon: 'fa-triangle-exclamation' },
  { key: 'concerns', title: 'Caregiver Concerns', icon: 'fa-circle-exclamation' },
  { key: 'diabetes', title: 'Diabetes', icon: 'fa-droplet', condition: 'diabetes' },
  { key: 'dementia', title: "Dementia", icon: 'fa-brain', condition: 'dementia' },
  { key: 'copd', title: 'Respiratory', icon: 'fa-lungs', condition: 'copd' },
  { key: 'cardiac', title: 'Cardiac', icon: 'fa-heart', condition: 'cardiac' },
  { key: 'parkinsons', title: "Parkinson's", icon: 'fa-bullseye', condition: 'parkinsons' },
  { key: 'stroke', title: 'Stroke', icon: 'fa-user-doctor', condition: 'stroke' },
  { key: 'summary', title: 'Daily Summary', icon: 'fa-pen-to-square' },
  { key: 'review', title: 'Review & Sign', icon: 'fa-signature' },
];

const OVERALL_STATUS_OPTIONS = [
  { value: 'doing_well', label: 'Doing Well', color: '#16a34a', desc: 'No noticeable concerns', icon: '🟢' },
  { value: 'slight_change', label: 'Slight Change', color: '#f59e0b', desc: 'Minor change from normal', icon: '🟡' },
  { value: 'significant_change', label: 'Significant Change', color: '#ea580c', desc: 'Noticeable concern requiring attention', icon: '🟠' },
  { value: 'urgent', label: 'Urgent Concern', color: '#dc2626', desc: 'Serious condition or incident', icon: '🔴' },
];
const APPEARANCE_OPTIONS = ['Alert','Comfortable','Happy','Calm','Tired','Weak','Pale','Sweaty','Confused','Anxious','Agitated','Sleepy','Short of breath','In pain'];
const MOODS = ['😊 Happy','🙂 Content','😐 Neutral','😟 Anxious','😢 Sad','😠 Irritable','😡 Agitated','😴 Withdrawn','❓ Unable to determine'];
const BEHAVIORS = ['Cooperative','Social','Talkative','Quiet','Withdrawn','Restless','Agitated','Aggressive','Crying','Repetitive','Refusing care'];
const AMOUNTS = ['100%','75%','50%','25%','None','N/A'];
const APPETITES = ['Excellent','Good','Fair','Poor','Refused food'];
const EATING_CONCERNS = ['None','Difficulty chewing','Difficulty swallowing','Coughing during meals','Nausea','Vomiting','Poor appetite','Food refusal'];
const PAIN_LOCATIONS = ['Head','Neck','Shoulder','Arm','Chest','Abdomen','Back','Hip','Knee','Leg','Foot','Other'];
const PAIN_DESCS = ['Aching','Sharp','Burning','Throbbing','Cramping','Pressure','Soreness'];
const PAIN_ACTIONS = ['Rest','Repositioned','Heat/cold per care plan','PRN med documented','Family notified','Nurse/supervisor notified','Provider notified'];
const FALL_LOCATIONS = ['Bedroom','Bathroom','Kitchen','Living room','Hallway','Outdoors','Vehicle','Other'];
const FALL_ACTIVITIES = ['Walking','Standing','Sitting down','Getting out of bed','Getting out of chair','Toileting','Bathing','Transferring','Other'];
const FALL_CAUSES = ['Trip','Slip','Loss of balance','Weakness','Dizziness','Transfer difficulty','Unknown','Other'];
const FALL_INJURIES = ['None observed','Bruising','Cut','Swelling','Pain','Head impact','Unable to bear weight'];
const FALL_NOTIFICATIONS = ['Family','Supervisor','Nurse','Provider','911 / EMS'];

const MENTAL_STATUS = ['Alert','Oriented to person','Oriented to place','Oriented to time','Forgetful','Confused','Disoriented','Repetitive questions','Difficulty communicating','Unusually sleepy','Agitated','Hallucinations','Wandering','No noticeable change'];
const FLUID_LEVELS = ['Excellent','Good','Fair','Poor','Refusing fluids'];
const FLUID_SIGNS = ['None','Dry mouth','Increased thirst','Decreased intake','Difficulty swallowing liquids','Other'];
const MEDS_HANDLED = ['Yes','No','Not applicable'];
const MEDS_STATUS = ['Taken/documented as scheduled','Refused','Missed','Unavailable','PRN documented','Concern'];
const MEDS_NOTIFY = ['Family','Supervisor','Nurse','Provider','None required'];
const MOBILITY_LEVELS = ['Independent','Independent with cane','Independent with walker','Uses wheelchair','Requires standby assistance','Requires one-person assistance','Requires two-person assistance','Bedbound','Other'];
const MOBILITY_VS = ['Better','Same','Slightly worse','Significantly worse'];
const MOBILITY_CONCERNS = ['None','Weakness','Unsteady gait','Dizziness','Trouble standing','Trouble transferring','Limping','Fear of falling','Other'];
const ADL_LEVELS = ['Independent','Assisted','Fully assisted','Refused','Not completed','Not scheduled'];
const ADL_FIELDS = [
  { key: 'adl_bathing', label: 'Bathing' },
  { key: 'adl_dressing', label: 'Dressing' },
  { key: 'adl_grooming', label: 'Grooming' },
  { key: 'adl_oral_care', label: 'Oral Care' },
  { key: 'adl_toileting', label: 'Toileting' },
  { key: 'adl_transfer', label: 'Transfer Assistance' },
  { key: 'adl_meal', label: 'Meal Assistance' },
];
const URINATION = ['Normal','Increased','Decreased','Difficulty','Pain reported','Incontinence','Strong odor/change','Other'];
const BOWEL_YN = ['Yes','No','Unknown'];
const BOWEL_CONSISTENCY = ['Normal','Hard','Loose','Diarrhea','Other'];
const BOWEL_CONCERNS = ['None','Constipation','Diarrhea','Incontinence','Abdominal discomfort','Other'];
const SKIN_CONDITIONS = ['No noticeable concerns','Redness','Bruising','Rash','Swelling','Skin tear','Wound','Pressure-area concern','Dry skin','Other'];
const SKIN_NEW = ['No','Yes','Unsure'];
const SLEEP_QUALITY = ['Excellent','Good','Fair','Poor','Unknown'];
const SLEEP_ISSUES = ['None','Difficulty falling asleep','Woke frequently','Restless','Daytime sleeping','Nighttime wandering','Nightmares','Other'];
const ACTIVITIES = ['Walking','Exercise','Physical therapy','Occupational therapy','Outside time','Television','Reading','Music','Games','Family visit','Phone/video call','Religious activity','Community outing','Medical appointment','Shopping','Meal outing','Other'];
const ENGAGEMENT = ['Very engaged','Engaged','Somewhat engaged','Little engagement','Refused activities'];
const CONCERN_CATEGORIES = ['Physical condition','Mental status','Mood/behavior','Pain','Medication','Eating','Drinking','Mobility','Fall risk','Skin/wound','Toileting','Sleep','Safety','Home environment','Other'];
const CONCERN_NOTIFIED = ['Family','Supervisor','Nurse','Physician/provider','Emergency services','No'];

// Phase 3 constants
const GLUCOSE_CONTEXT = ['Fasting','Before meal','After meal','Bedtime','Other'];
const DIABETES_SYMPTOMS = ['None','Shaking','Sweating','Weakness','Confusion','Increased thirst','Frequent urination','Unusual fatigue','Other'];
const FOOT_OBS = ['No noticeable concern','Redness','Blister','Cut','Swelling','Other'];
const COGNITIVE_BASELINE = ['Usual baseline','Slightly more confused','Significantly more confused','More alert than usual'];
const DEMENTIA_BEHAVIORS = ['Wandering','Repeating questions','Agitation','Resistance to care','Sundowning','Hallucinations','Sleep disturbance','Anxiety','Aggression','No unusual behavior'];
const DEMENTIA_SAFETY = ['None','Exit seeking','Stove/appliance concern','Medication concern','Wandering','Fall risk','Other'];
const COPD_BREATHING = ['Normal for client','Mild shortness of breath','Moderate SOB','Significant breathing concern'];
const COPD_COUGH = ['None','Usual','Increased','Productive','Dry'];
const COPD_OXYGEN_USED = ['Not prescribed/used','Used per care plan'];
const SWELLING_LOCATIONS = ['None','Ankles','Feet','Legs','Hands','Other'];
const SWELLING_SEVERITY = ['Mild','Moderate','Significant'];
const CARDIAC_BREATHING = ['Normal','SOB with activity','SOB at rest','Other change'];
const CARDIAC_FATIGUE = ['Normal','Increased','Significant'];
const TREMOR_SEVERITY = ['None','Mild','Moderate','Significant'];
const PARK_MOVEMENT = ['Normal for client','Slower','Stiff','Freezing episodes','Difficulty initiating'];
const PARK_BALANCE = ['Stable','Slightly unstable','Significantly unstable'];
const PARK_SWALLOWING = ['No noticeable difficulty','Difficulty observed'];
const PARK_SPEECH = ['Normal for client','Softer','Difficult to understand'];
const STRENGTH_LEVELS = ['Normal','Reduced'];
const STROKE_SPEECH = ['Normal for client','Difficulty speaking','Slurred','Difficulty understanding'];
const STROKE_SWALLOWING = ['No noticeable issue','Concern observed'];
const THERAPY_STATUS = ['Exercises completed','Partially completed','Refused','Not scheduled'];

const CareLogWizard = ({ user, onLogout }) => {
  const { clientId, logId } = useParams();
  const [searchParams] = useSearchParams();
  const prefillMode = searchParams.get('prefill') === 'last';
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [client, setClient] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logIdState, setLogIdState] = useState(logId || null);
  const [data, setData] = useState({
    log_date: new Date().toISOString().slice(0, 10),
    arrival_time: '',
    departure_time: '',
    shift_type: '',
    location: 'Client Home',
    overall_status: '',
    baseline_comparison: '',
    appearance: [],
    caregiver_observation: '',
    bp_systolic: '', bp_diastolic: '', bp_position: '',
    pulse: '', pulse_rhythm: '',
    temperature: '', temperature_method: '',
    respiratory_rate: '', oxygen_saturation: '',
    on_oxygen: false, oxygen_flow_rate: '',
    weight: '', blood_glucose: '', glucose_timing: '',
    mood: '', behaviors: [], behavior_notes: '',
    pain_present: false, pain_level: 0, pain_location: '',
    pain_description: [], pain_vs_usual: '', pain_action_taken: [],
    breakfast_amount: '', lunch_amount: '', dinner_amount: '',
    snacks: '', appetite: '', eating_concerns: [], nutrition_notes: '',
    fall_today: false, near_fall: false, other_incident: 'No',
    fall_time: '', fall_location: '', fall_witnessed: false,
    fall_activity: '', fall_cause: '', fall_injury: [], hit_head: '',
    fall_narrative: '', fall_notifications: [],
    daily_summary: '', concerns_level: '', concerns_notes: '',
    mental_status: [], mental_change: '', mental_change_notes: '',
    fluid_intake_level: '', water_oz: '', other_fluids_oz: '', fluid_signs: [],
    meds_handled: '', meds_status: [], meds_notes: '', meds_notification: [], meds_time: '',
    mobility_level: '', mobility_vs_normal: '', mobility_concerns: [],
    adl_bathing: '', adl_dressing: '', adl_grooming: '', adl_oral_care: '',
    adl_toileting: '', adl_transfer: '', adl_meal: '',
    urination_status: [], bowel_movement: '', bowel_consistency: '', bowel_concerns: [],
    skin_condition: [], skin_location: '', skin_new: '', skin_notes: '',
    sleep_quality: '', sleep_hours: '', sleep_issues: [],
    activities_completed: [], engagement_level: '',
    concern_category: [], concerns_notified: [],
    skin_photo: null,
    glucose_context: '', diabetes_symptoms: [], foot_observation: [], insulin_documented: '',
    cognitive_baseline: '', dementia_behaviors: [], recognized_family: false,
    recognized_caregiver: false, recognized_environment: false, dementia_safety: [],
    copd_breathing: '', copd_cough: '', copd_sputum_change: '', copd_oxygen_used: '',
    swelling_locations: [], swelling_severity: '', cardiac_breathing: '', cardiac_fatigue: '',
    tremor_severity: '', parkinsons_movement: '', parkinsons_balance: '',
    parkinsons_swallowing: '', parkinsons_speech: '',
    strength_la: '', strength_ra: '', strength_ll: '', strength_rl: '',
    stroke_speech: '', stroke_swallowing: '', therapy_status: '',
    signed_by: user?.name || '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/clients/${clientId}`),
          axios.get(`${API}/clients/${clientId}/care-plan`).catch(() => ({ data: null })),
        ]);
        setClient(c.data);
        setCarePlan(p.data);
      } catch { toast.error('Client not found'); navigate('/clients'); }
    })();
    if (logId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/care-logs/${logId}`);
          setData(prev => ({ ...prev, ...res.data }));
        } catch { toast.error('Log not found'); }
      })();
    } else if (prefillMode) {
      (async () => {
        try {
          const res = await axios.get(`${API}/clients/${clientId}/care-logs/latest`);
          setData(prev => ({ ...prev, ...res.data }));
          toast.success('Prefilled from last completed log — review and update what changed.');
        } catch { toast.error('No previous log to prefill from'); }
      })();
    }
  }, [clientId, logId, navigate, prefillMode]);

  const update = (patch) => setData(prev => ({ ...prev, ...patch }));
  const toggle = (field, val) => setData(prev => ({
    ...prev,
    [field]: prev[field].includes(val) ? prev[field].filter(x => x !== val) : [...prev[field], val]
  }));

  const buildPayload = () => {
    const p = { ...data, client_id: clientId };
    ['bp_systolic','bp_diastolic','pulse','respiratory_rate','oxygen_saturation','blood_glucose','pain_level']
      .forEach(k => { p[k] = p[k] === '' || p[k] === null ? null : parseInt(p[k], 10); });
    ['temperature','oxygen_flow_rate','weight','water_oz','other_fluids_oz','sleep_hours'].forEach(k => {
      p[k] = p[k] === '' || p[k] === null ? null : parseFloat(p[k]);
    });
    return p;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { ...buildPayload(), status: 'in_progress' };
      if (logIdState) {
        await axios.put(`${API}/care-logs/${logIdState}`, payload);
      } else {
        const res = await axios.post(`${API}/clients/${clientId}/care-logs`, payload);
        setLogIdState(res.data.id);
      }
      toast.success('Draft saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const submitLog = async () => {
    setSaving(true);
    try {
      const payload = { ...buildPayload(), status: 'completed' };
      if (logIdState) {
        await axios.put(`${API}/care-logs/${logIdState}`, payload);
      } else {
        await axios.post(`${API}/clients/${clientId}/care-logs`, payload);
      }
      toast.success('Care log signed & submitted');
      navigate(`/clients/${clientId}/care-logs`);
    } catch { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };

  const next = () => setStep(s => Math.min(s + 1, visibleSteps.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  // Filter steps based on care plan's enabled_sections + condition_modules
  const enabledSet = new Set(carePlan?.enabled_sections || STEPS.filter(s => !s.condition).map(s => s.key));
  enabledSet.add('review');
  const conditionSet = new Set(carePlan?.condition_modules || []);
  const visibleSteps = STEPS.filter(s => {
    if (s.condition) return conditionSet.has(s.condition);
    return enabledSet.has(s.key);
  });
  const currentKey = visibleSteps[step]?.key;
  const enabledVitals = new Set(carePlan?.enabled_vitals || ['bp', 'pulse', 'temperature', 'spo2', 'weight', 'glucose', 'rr']);

  const Card = ({ selected, onClick, children, testId, style }) => (
    <button type="button" onClick={onClick} data-testid={testId} className={`clog-option-card ${selected ? 'selected' : ''}`} style={style}>
      {children}
    </button>
  );

  const Chip = ({ selected, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId} className={`clog-chip ${selected ? 'selected' : ''}`}>
      {children}
    </button>
  );

  const completeness = () => {
    const checks = {
      Visit: !!data.arrival_time,
      Status: !!data.overall_status,
      Vitals: !!(data.bp_systolic || data.pulse || data.temperature || data.oxygen_saturation),
      Mood: !!data.mood,
      Pain: data.pain_present !== undefined,
      Meals: !!data.appetite || !!data.breakfast_amount,
      Fall: data.fall_today !== undefined,
      Summary: !!data.daily_summary,
    };
    return checks;
  };

  if (!client) return <div className="loading-screen">Loading client…</div>;

  return (
    <div className="dashboard-container" data-testid="care-log-wizard">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <div className="clog-header">
          <button onClick={() => navigate(`/clients/${clientId}/care-logs`)} className="clog-back-btn" data-testid="clog-back-btn">
            <i className="fa-solid fa-arrow-left" /> Back to logs
          </button>
          <div>
            <h1 className="clog-client-name">{client.name}</h1>
            <p className="clog-client-meta">
              {data.log_date} · Caregiver: {user?.name || 'You'}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className="clog-stepper" data-testid="clog-stepper">
          {visibleSteps.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStep(i)}
              className={`clog-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              data-testid={`clog-step-${s.key}`}
            >
              <i className={`fa-solid ${s.icon}`} />
              <span>{s.title}</span>
            </button>
          ))}
        </div>

        <div className="section-card clog-body">
          {/* STEP — VISIT INFO */}
          {currentKey === 'visit' && (
            <div>
              <h2 className="clog-step-title">Visit Information</h2>
              <div className="clog-grid-2">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={data.log_date} onChange={e => update({ log_date: e.target.value })} data-testid="clog-date" />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <select value={data.location} onChange={e => update({ location: e.target.value })} data-testid="clog-location">
                    {['Client Home','Family Home','Assisted Living','Hospital','Appointment','Outing','Other'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Arrival Time</label>
                  <input type="time" value={data.arrival_time} onChange={e => update({ arrival_time: e.target.value })} data-testid="clog-arrival" />
                </div>
                <div className="form-group">
                  <label>Departure Time</label>
                  <input type="time" value={data.departure_time} onChange={e => update({ departure_time: e.target.value })} data-testid="clog-departure" />
                </div>
                <div className="form-group">
                  <label>Shift Type</label>
                  <select value={data.shift_type} onChange={e => update({ shift_type: e.target.value })} data-testid="clog-shift">
                    <option value="">Select…</option>
                    {['Morning','Afternoon','Evening','Overnight','Live-in','Other'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP — OVERALL STATUS */}
          {currentKey === 'status' && (
            <div>
              <h2 className="clog-step-title">How is {client.name} today?</h2>
              <div className="clog-status-grid">
                {OVERALL_STATUS_OPTIONS.map(opt => (
                  <Card key={opt.value} selected={data.overall_status === opt.value}
                    onClick={() => update({ overall_status: opt.value })}
                    testId={`clog-status-${opt.value}`}
                    style={{ borderColor: data.overall_status === opt.value ? opt.color : '#e2e8f0' }}>
                    <div style={{ fontSize: '1.8rem' }}>{opt.icon}</div>
                    <div style={{ fontWeight: 700, color: opt.color, marginTop: 4 }}>{opt.label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b8899' }}>{opt.desc}</div>
                  </Card>
                ))}
              </div>
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Compared with normal baseline</label>
                <select value={data.baseline_comparison} onChange={e => update({ baseline_comparison: e.target.value })} data-testid="clog-baseline">
                  <option value="">Select…</option>
                  {['No change','Slight improvement','Significant improvement','Slight decline','Significant decline','Unsure'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Overall appearance (check all that apply)</label>
                <div className="clog-chip-row">
                  {APPEARANCE_OPTIONS.map(a => (
                    <Chip key={a} selected={data.appearance.includes(a)} onClick={() => toggle('appearance', a)} testId={`clog-appear-${a.replace(/\s/g,'-').toLowerCase()}`}>{a}</Chip>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Caregiver observation</label>
                <textarea value={data.caregiver_observation} onChange={e => update({ caregiver_observation: e.target.value })} rows={3} data-testid="clog-observation" placeholder="Describe how the client appears today…" />
              </div>
            </div>
          )}

          {/* STEP — VITALS */}
          {currentKey === 'vitals' && (
            <div>
              <h2 className="clog-step-title">Today's Vitals</h2>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '-0.5rem' }}>All fields optional — record what applies to this client's care plan.</p>
              <div className="clog-grid-2">
                <div className="form-group">
                  <label>Blood Pressure (mmHg)</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="number" placeholder="Systolic" value={data.bp_systolic} onChange={e => update({ bp_systolic: e.target.value })} data-testid="clog-bp-sys" />
                    <span>/</span>
                    <input type="number" placeholder="Diastolic" value={data.bp_diastolic} onChange={e => update({ bp_diastolic: e.target.value })} data-testid="clog-bp-dia" />
                  </div>
                  <select value={data.bp_position} onChange={e => update({ bp_position: e.target.value })} style={{ marginTop: 6 }}>
                    <option value="">Position…</option>{['Sitting','Standing','Lying down'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Pulse (bpm)</label>
                  <input type="number" value={data.pulse} onChange={e => update({ pulse: e.target.value })} data-testid="clog-pulse" />
                  <select value={data.pulse_rhythm} onChange={e => update({ pulse_rhythm: e.target.value })} style={{ marginTop: 6 }}>
                    <option value="">Rhythm…</option>{['Regular','Irregular','Not assessed'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Temperature (°F)</label>
                  <input type="number" step="0.1" value={data.temperature} onChange={e => update({ temperature: e.target.value })} data-testid="clog-temp" />
                  <select value={data.temperature_method} onChange={e => update({ temperature_method: e.target.value })} style={{ marginTop: 6 }}>
                    <option value="">Method…</option>{['Oral','Forehead','Ear','Axillary','Other'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Respiratory Rate (breaths/min)</label>
                  <input type="number" value={data.respiratory_rate} onChange={e => update({ respiratory_rate: e.target.value })} data-testid="clog-rr" />
                </div>
                <div className="form-group">
                  <label>Oxygen Saturation (%)</label>
                  <input type="number" value={data.oxygen_saturation} onChange={e => update({ oxygen_saturation: e.target.value })} data-testid="clog-spo2" />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <input type="checkbox" checked={data.on_oxygen} onChange={e => update({ on_oxygen: e.target.checked })} data-testid="clog-on-oxygen" />
                    On oxygen
                  </label>
                  {data.on_oxygen && (
                    <input type="number" step="0.1" placeholder="Flow rate L/min" value={data.oxygen_flow_rate} onChange={e => update({ oxygen_flow_rate: e.target.value })} style={{ marginTop: 6 }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Weight (lb)</label>
                  <input type="number" step="0.1" value={data.weight} onChange={e => update({ weight: e.target.value })} data-testid="clog-weight" />
                </div>
                <div className="form-group">
                  <label>Blood Glucose (mg/dL)</label>
                  <input type="number" value={data.blood_glucose} onChange={e => update({ blood_glucose: e.target.value })} data-testid="clog-glucose" />
                  <select value={data.glucose_timing} onChange={e => update({ glucose_timing: e.target.value })} style={{ marginTop: 6 }}>
                    <option value="">Timing…</option>{['Fasting','Before breakfast','After breakfast','Before lunch','After lunch','Before dinner','After dinner','Bedtime','Other'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                <i className="fa-solid fa-circle-info" /> CareGuard stores raw values only. Family sees numbers; any clinical interpretation is the responsibility of the care plan/provider.
              </p>
            </div>
          )}

          {/* STEP — MOOD */}
          {currentKey === 'mental' && (
            <div>
              <h2 className="clog-step-title">Mental & Cognitive Status</h2>
              <div className="form-group">
                <label>Mental status (check all that apply)</label>
                <div className="clog-chip-row">{MENTAL_STATUS.map(m => <Chip key={m} selected={data.mental_status.includes(m)} onClick={() => toggle('mental_status', m)}>{m}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Change from normal?</label>
                <div className="clog-chip-row">{['No','Yes — Minor','Yes — Significant'].map(v => <Chip key={v} selected={data.mental_change === v} onClick={() => update({ mental_change: v })}>{v}</Chip>)}</div>
              </div>
              {data.mental_change && data.mental_change !== 'No' && (
                <div className="form-group">
                  <label>Describe the change</label>
                  <textarea rows={3} value={data.mental_change_notes} onChange={e => update({ mental_change_notes: e.target.value })} data-testid="clog-mental-notes" />
                </div>
              )}
            </div>
          )}

          {currentKey === 'mood' && (
            <div>
              <h2 className="clog-step-title">Mood & Behavior</h2>
              <div className="form-group">
                <label>Mood today</label>
                <div className="clog-chip-row">
                  {MOODS.map(m => <Chip key={m} selected={data.mood === m} onClick={() => update({ mood: m })} testId={`clog-mood-${m.slice(0,5)}`}>{m}</Chip>)}
                </div>
              </div>
              <div className="form-group">
                <label>Behaviors observed</label>
                <div className="clog-chip-row">
                  {BEHAVIORS.map(b => <Chip key={b} selected={data.behaviors.includes(b)} onClick={() => toggle('behaviors', b)}>{b}</Chip>)}
                </div>
              </div>
              <div className="form-group">
                <label>Behavior notes</label>
                <textarea value={data.behavior_notes} onChange={e => update({ behavior_notes: e.target.value })} rows={3} />
              </div>
            </div>
          )}

          {/* STEP — PAIN */}
          {currentKey === 'pain' && (
            <div>
              <h2 className="clog-step-title">Pain Assessment</h2>
              <div className="clog-two-btn">
                <Card selected={!data.pain_present} onClick={() => update({ pain_present: false, pain_level: 0 })} testId="clog-pain-no">
                  <div style={{ fontSize: '1.6rem' }}>✅</div><div>No pain</div>
                </Card>
                <Card selected={data.pain_present} onClick={() => update({ pain_present: true })} testId="clog-pain-yes">
                  <div style={{ fontSize: '1.6rem' }}>⚠️</div><div>Yes, pain reported</div>
                </Card>
              </div>
              {data.pain_present && (
                <>
                  <div className="form-group" style={{ marginTop: '1.5rem' }}>
                    <label>Pain level: <strong>{data.pain_level}/10</strong></label>
                    <input type="range" min="0" max="10" value={data.pain_level} onChange={e => update({ pain_level: parseInt(e.target.value, 10) })} data-testid="clog-pain-level" style={{ width: '100%' }} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <select value={data.pain_location} onChange={e => update({ pain_location: e.target.value })}>
                      <option value="">Select…</option>{PAIN_LOCATIONS.map(l => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <div className="clog-chip-row">{PAIN_DESCS.map(p => <Chip key={p} selected={data.pain_description.includes(p)} onClick={() => toggle('pain_description', p)}>{p}</Chip>)}</div>
                  </div>
                  <div className="form-group">
                    <label>Compared with usual pain</label>
                    <select value={data.pain_vs_usual} onChange={e => update({ pain_vs_usual: e.target.value })}>
                      <option value="">Select…</option>{['Better','Same','Worse','New pain'].map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Action taken</label>
                    <div className="clog-chip-row">{PAIN_ACTIONS.map(a => <Chip key={a} selected={data.pain_action_taken.includes(a)} onClick={() => toggle('pain_action_taken', a)}>{a}</Chip>)}</div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP — MEALS */}
          {currentKey === 'meals' && (
            <div>
              <h2 className="clog-step-title">Meals & Nutrition</h2>
              {[
                { key: 'breakfast_amount', label: 'Breakfast' },
                { key: 'lunch_amount', label: 'Lunch' },
                { key: 'dinner_amount', label: 'Dinner' },
              ].map(({ key, label }) => (
                <div className="form-group" key={key}>
                  <label>{label}</label>
                  <div className="clog-chip-row">
                    {AMOUNTS.map(a => <Chip key={a} selected={data[key] === a} onClick={() => update({ [key]: a })} testId={`clog-${key}-${a}`}>{a}</Chip>)}
                  </div>
                </div>
              ))}
              <div className="form-group">
                <label>Snacks</label>
                <div className="clog-chip-row">{['Good','Some','None','N/A'].map(a => <Chip key={a} selected={data.snacks === a} onClick={() => update({ snacks: a })}>{a}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Overall appetite</label>
                <div className="clog-chip-row">{APPETITES.map(a => <Chip key={a} selected={data.appetite === a} onClick={() => update({ appetite: a })}>{a}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Eating concerns</label>
                <div className="clog-chip-row">{EATING_CONCERNS.map(c => <Chip key={c} selected={data.eating_concerns.includes(c)} onClick={() => toggle('eating_concerns', c)}>{c}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Nutrition notes</label>
                <textarea rows={2} value={data.nutrition_notes} onChange={e => update({ nutrition_notes: e.target.value })} />
              </div>
            </div>
          )}

          {/* STEP — FALL */}
          {currentKey === 'fluids' && (
            <div>
              <h2 className="clog-step-title">Fluid Intake</h2>
              <div className="form-group">
                <label>Overall fluid intake</label>
                <div className="clog-chip-row">{FLUID_LEVELS.map(f => <Chip key={f} selected={data.fluid_intake_level === f} onClick={() => update({ fluid_intake_level: f })}>{f}</Chip>)}</div>
              </div>
              <div className="clog-grid-2">
                <div className="form-group"><label>Water (oz)</label><input type="number" step="0.1" value={data.water_oz} onChange={e => update({ water_oz: e.target.value })} data-testid="clog-water-oz" /></div>
                <div className="form-group"><label>Other fluids (oz)</label><input type="number" step="0.1" value={data.other_fluids_oz} onChange={e => update({ other_fluids_oz: e.target.value })} /></div>
              </div>
              <div className="form-group">
                <label>Signs / concerns</label>
                <div className="clog-chip-row">{FLUID_SIGNS.map(f => <Chip key={f} selected={data.fluid_signs.includes(f)} onClick={() => toggle('fluid_signs', f)}>{f}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'meds' && (
            <div>
              <h2 className="clog-step-title">Medication Status</h2>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '-0.5rem' }}>
                <i className="fa-solid fa-circle-info" /> CareGuard documents medications per the care plan — it doesn't make medication decisions.
              </p>
              <div className="form-group">
                <label>Were scheduled medications handled per care plan?</label>
                <div className="clog-chip-row">{MEDS_HANDLED.map(m => <Chip key={m} selected={data.meds_handled === m} onClick={() => update({ meds_handled: m })} testId={`clog-meds-handled-${m}`}>{m}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Medication status (check all)</label>
                <div className="clog-chip-row">{MEDS_STATUS.map(m => <Chip key={m} selected={data.meds_status.includes(m)} onClick={() => toggle('meds_status', m)}>{m}</Chip>)}</div>
              </div>
              <div className="clog-grid-2">
                <div className="form-group"><label>Time</label><input type="time" value={data.meds_time} onChange={e => update({ meds_time: e.target.value })} /></div>
              </div>
              <div className="form-group">
                <label>Notifications made</label>
                <div className="clog-chip-row">{MEDS_NOTIFY.map(n => <Chip key={n} selected={data.meds_notification.includes(n)} onClick={() => toggle('meds_notification', n)}>{n}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea rows={2} value={data.meds_notes} onChange={e => update({ meds_notes: e.target.value })} />
              </div>
            </div>
          )}

          {currentKey === 'mobility' && (
            <div>
              <h2 className="clog-step-title">Mobility</h2>
              <div className="form-group">
                <label>Mobility today</label>
                <div className="clog-chip-row">{MOBILITY_LEVELS.map(m => <Chip key={m} selected={data.mobility_level === m} onClick={() => update({ mobility_level: m })}>{m}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Compared with normal</label>
                <div className="clog-chip-row">{MOBILITY_VS.map(m => <Chip key={m} selected={data.mobility_vs_normal === m} onClick={() => update({ mobility_vs_normal: m })}>{m}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Mobility concerns</label>
                <div className="clog-chip-row">{MOBILITY_CONCERNS.map(c => <Chip key={c} selected={data.mobility_concerns.includes(c)} onClick={() => toggle('mobility_concerns', c)}>{c}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'adls' && (
            <div>
              <h2 className="clog-step-title">Activities of Daily Living</h2>
              {ADL_FIELDS.map(f => (
                <div className="form-group" key={f.key}>
                  <label>{f.label}</label>
                  <div className="clog-chip-row">
                    {ADL_LEVELS.map(l => <Chip key={l} selected={data[f.key] === l} onClick={() => update({ [f.key]: l })}>{l}</Chip>)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {currentKey === 'toileting' && (
            <div>
              <h2 className="clog-step-title">Toileting & Elimination</h2>
              <div className="form-group">
                <label>Urination</label>
                <div className="clog-chip-row">{URINATION.map(u => <Chip key={u} selected={data.urination_status.includes(u)} onClick={() => toggle('urination_status', u)}>{u}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Bowel movement today?</label>
                <div className="clog-chip-row">{BOWEL_YN.map(b => <Chip key={b} selected={data.bowel_movement === b} onClick={() => update({ bowel_movement: b })}>{b}</Chip>)}</div>
              </div>
              {data.bowel_movement === 'Yes' && (
                <div className="form-group">
                  <label>Consistency</label>
                  <div className="clog-chip-row">{BOWEL_CONSISTENCY.map(b => <Chip key={b} selected={data.bowel_consistency === b} onClick={() => update({ bowel_consistency: b })}>{b}</Chip>)}</div>
                </div>
              )}
              <div className="form-group">
                <label>Bowel concerns</label>
                <div className="clog-chip-row">{BOWEL_CONCERNS.map(b => <Chip key={b} selected={data.bowel_concerns.includes(b)} onClick={() => toggle('bowel_concerns', b)}>{b}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'skin' && (
            <div>
              <h2 className="clog-step-title">Skin & Body Check</h2>
              <div className="form-group">
                <label>Skin condition</label>
                <div className="clog-chip-row">{SKIN_CONDITIONS.map(s => <Chip key={s} selected={data.skin_condition.includes(s)} onClick={() => toggle('skin_condition', s)}>{s}</Chip>)}</div>
              </div>
              <div className="clog-grid-2">
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" placeholder="e.g., Left heel, Sacrum" value={data.skin_location} onChange={e => update({ skin_location: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Is this new?</label>
                  <div className="clog-chip-row">{SKIN_NEW.map(s => <Chip key={s} selected={data.skin_new === s} onClick={() => update({ skin_new: s })}>{s}</Chip>)}</div>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <div className="clog-textarea-wrap">
                  <textarea rows={3} value={data.skin_notes} onChange={e => update({ skin_notes: e.target.value })} />
                  <VoiceInput value={data.skin_notes} onChange={v => update({ skin_notes: v })} testId="voice-skin-notes" />
                </div>
              </div>
              <div className="form-group">
                <label>Photo <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — only if consented by org)</span></label>
                {data.skin_photo && (
                  <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
                    <img src={data.skin_photo} alt="Skin check" style={{ maxWidth: 200, borderRadius: 8, border: '1px solid #e2e8f0' }} data-testid="clog-skin-photo-preview" />
                    <button type="button" onClick={() => update({ skin_photo: null })} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(220,38,38,0.9)', color: '#fff', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer' }} data-testid="clog-skin-photo-remove">
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  data-testid="clog-skin-photo-input"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2 MB'); return; }
                    const reader = new FileReader();
                    reader.onload = ev => update({ skin_photo: ev.target.result });
                    reader.readAsDataURL(f);
                  }}
                />
              </div>
            </div>
          )}

          {currentKey === 'sleep' && (
            <div>
              <h2 className="clog-step-title">Sleep</h2>
              <div className="clog-grid-2">
                <div className="form-group">
                  <label>How did the client sleep?</label>
                  <div className="clog-chip-row">{SLEEP_QUALITY.map(s => <Chip key={s} selected={data.sleep_quality === s} onClick={() => update({ sleep_quality: s })}>{s}</Chip>)}</div>
                </div>
                <div className="form-group">
                  <label>Approximate hours</label>
                  <input type="number" step="0.5" value={data.sleep_hours} onChange={e => update({ sleep_hours: e.target.value })} data-testid="clog-sleep-hours" />
                </div>
              </div>
              <div className="form-group">
                <label>Sleep issues</label>
                <div className="clog-chip-row">{SLEEP_ISSUES.map(s => <Chip key={s} selected={data.sleep_issues.includes(s)} onClick={() => toggle('sleep_issues', s)}>{s}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'activities' && (
            <div>
              <h2 className="clog-step-title">Activities & Social Engagement</h2>
              <div className="form-group">
                <label>Activities completed today</label>
                <div className="clog-chip-row">{ACTIVITIES.map(a => <Chip key={a} selected={data.activities_completed.includes(a)} onClick={() => toggle('activities_completed', a)}>{a}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Engagement level</label>
                <div className="clog-chip-row">{ENGAGEMENT.map(e => <Chip key={e} selected={data.engagement_level === e} onClick={() => update({ engagement_level: e })}>{e}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'fall' && (
            <div>
              <h2 className="clog-step-title">Fall & Incident Check</h2>
              <div className="form-group">
                <label>Did the client fall today?</label>
                <div className="clog-two-btn">
                  <Card selected={!data.fall_today} onClick={() => update({ fall_today: false })} testId="clog-fall-no"><div style={{ fontSize: '1.4rem' }}>✅</div><div>NO</div></Card>
                  <Card selected={data.fall_today} onClick={() => update({ fall_today: true })} testId="clog-fall-yes"><div style={{ fontSize: '1.4rem' }}>⚠️</div><div>YES</div></Card>
                </div>
              </div>
              <div className="form-group">
                <label>Any near-fall?</label>
                <div className="clog-two-btn">
                  <Card selected={!data.near_fall} onClick={() => update({ near_fall: false })}><div>NO</div></Card>
                  <Card selected={data.near_fall} onClick={() => update({ near_fall: true })}><div>YES</div></Card>
                </div>
              </div>
              <div className="form-group">
                <label>Any other incident?</label>
                <select value={data.other_incident} onChange={e => update({ other_incident: e.target.value })}>
                  {['No','Injury','Medication incident','Behavioral incident','Wandering/elopement','Property/safety issue','Emergency medical event','Other'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              {data.fall_today && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }} data-testid="clog-fall-report">
                  <h3 style={{ color: '#b91c1c', marginTop: 0 }}><i className="fa-solid fa-triangle-exclamation" /> Fall Report</h3>
                  <div className="clog-grid-2">
                    <div className="form-group"><label>Time</label><input type="time" value={data.fall_time} onChange={e => update({ fall_time: e.target.value })} /></div>
                    <div className="form-group"><label>Location</label>
                      <select value={data.fall_location} onChange={e => update({ fall_location: e.target.value })}>
                        <option value="">Select…</option>{FALL_LOCATIONS.map(l => <option key={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Witnessed?</label>
                      <div className="clog-two-btn">
                        <Card selected={data.fall_witnessed} onClick={() => update({ fall_witnessed: true })}>Yes</Card>
                        <Card selected={!data.fall_witnessed} onClick={() => update({ fall_witnessed: false })}>No</Card>
                      </div>
                    </div>
                    <div className="form-group"><label>What was client doing?</label>
                      <select value={data.fall_activity} onChange={e => update({ fall_activity: e.target.value })}>
                        <option value="">Select…</option>{FALL_ACTIVITIES.map(a => <option key={a}>{a}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Possible cause</label>
                      <select value={data.fall_cause} onChange={e => update({ fall_cause: e.target.value })}>
                        <option value="">Select…</option>{FALL_CAUSES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Hit head?</label>
                      <select value={data.hit_head} onChange={e => update({ hit_head: e.target.value })}>
                        <option value="">Select…</option>{['Yes','No','Unknown'].map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Possible injury (check all)</label>
                    <div className="clog-chip-row">{FALL_INJURIES.map(inj => <Chip key={inj} selected={data.fall_injury.includes(inj)} onClick={() => toggle('fall_injury', inj)}>{inj}</Chip>)}</div>
                  </div>
                  <div className="form-group">
                    <label>Notifications made</label>
                    <div className="clog-chip-row">{FALL_NOTIFICATIONS.map(n => <Chip key={n} selected={data.fall_notifications.includes(n)} onClick={() => toggle('fall_notifications', n)}>{n}</Chip>)}</div>
                  </div>
                  <div className="form-group">
                    <label>Fall narrative (describe what occurred)</label>
                    <textarea rows={4} value={data.fall_narrative} onChange={e => update({ fall_narrative: e.target.value })} data-testid="clog-fall-narrative" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP — SUMMARY */}
          {currentKey === 'concerns' && (
            <div>
              <h2 className="clog-step-title">Caregiver Concerns</h2>
              <div className="form-group">
                <label>Concern category (check all)</label>
                <div className="clog-chip-row">{CONCERN_CATEGORIES.map(c => <Chip key={c} selected={data.concern_category.includes(c)} onClick={() => toggle('concern_category', c)}>{c}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Was anyone notified?</label>
                <div className="clog-chip-row">{CONCERN_NOTIFIED.map(n => <Chip key={n} selected={data.concerns_notified.includes(n)} onClick={() => toggle('concerns_notified', n)}>{n}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'summary' && (
            <div>
              <h2 className="clog-step-title">How was your client today?</h2>
              <div className="form-group">
                <label>Daily summary</label>
                <div className="clog-textarea-wrap">
                  <textarea rows={6} value={data.daily_summary} onChange={e => update({ daily_summary: e.target.value })} data-testid="clog-summary"
                    placeholder="Describe the client's overall day, positive activities, changes from normal, concerns, and anything the family should know." />
                  <VoiceInput value={data.daily_summary} onChange={v => update({ daily_summary: v })} testId="voice-daily-summary" />
                </div>
              </div>
              <div className="form-group">
                <label>Caregiver concerns level</label>
                <div className="clog-chip-row">
                  {['No concerns','Minor','Important','Urgent'].map(c => <Chip key={c} selected={data.concerns_level === c} onClick={() => update({ concerns_level: c })}>{c}</Chip>)}
                </div>
              </div>
              {data.concerns_level && data.concerns_level !== 'No concerns' && (
                <div className="form-group">
                  <label>Describe concern</label>
                  <div className="clog-textarea-wrap">
                    <textarea rows={3} value={data.concerns_notes} onChange={e => update({ concerns_notes: e.target.value })} />
                    <VoiceInput value={data.concerns_notes} onChange={v => update({ concerns_notes: v })} testId="voice-concerns-notes" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PHASE 3 — CONDITION MODULES */}
          {currentKey === 'diabetes' && (
            <div data-testid="clog-module-diabetes">
              <h2 className="clog-step-title">Diabetes Module</h2>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '-0.5rem' }}>See Vitals step for glucose reading. Extra fields below.</p>
              <div className="form-group">
                <label>Glucose reading context</label>
                <div className="clog-chip-row">{GLUCOSE_CONTEXT.map(g => <Chip key={g} selected={data.glucose_context === g} onClick={() => update({ glucose_context: g })}>{g}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Symptoms observed</label>
                <div className="clog-chip-row">{DIABETES_SYMPTOMS.map(s => <Chip key={s} selected={data.diabetes_symptoms.includes(s)} onClick={() => toggle('diabetes_symptoms', s)}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Foot observation</label>
                <div className="clog-chip-row">{FOOT_OBS.map(f => <Chip key={f} selected={data.foot_observation.includes(f)} onClick={() => toggle('foot_observation', f)}>{f}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Medication/insulin documented per care plan?</label>
                <div className="clog-chip-row">{['Yes','No','Not applicable'].map(v => <Chip key={v} selected={data.insulin_documented === v} onClick={() => update({ insulin_documented: v })}>{v}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'dementia' && (
            <div data-testid="clog-module-dementia">
              <h2 className="clog-step-title">Dementia / Alzheimer's Module</h2>
              <div className="form-group">
                <label>Cognitive status vs. baseline</label>
                <div className="clog-chip-row">{COGNITIVE_BASELINE.map(c => <Chip key={c} selected={data.cognitive_baseline === c} onClick={() => update({ cognitive_baseline: c })}>{c}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Behaviors observed</label>
                <div className="clog-chip-row">{DEMENTIA_BEHAVIORS.map(b => <Chip key={b} selected={data.dementia_behaviors.includes(b)} onClick={() => toggle('dementia_behaviors', b)}>{b}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Client recognized:</label>
                <div className="clog-chip-row">
                  <Chip selected={data.recognized_family} onClick={() => update({ recognized_family: !data.recognized_family })}>Family</Chip>
                  <Chip selected={data.recognized_caregiver} onClick={() => update({ recognized_caregiver: !data.recognized_caregiver })}>Caregiver</Chip>
                  <Chip selected={data.recognized_environment} onClick={() => update({ recognized_environment: !data.recognized_environment })}>Home/environment</Chip>
                </div>
              </div>
              <div className="form-group">
                <label>Safety issues</label>
                <div className="clog-chip-row">{DEMENTIA_SAFETY.map(s => <Chip key={s} selected={data.dementia_safety.includes(s)} onClick={() => toggle('dementia_safety', s)}>{s}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'copd' && (
            <div data-testid="clog-module-copd">
              <h2 className="clog-step-title">COPD / Respiratory Module</h2>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '-0.5rem' }}>Respiratory rate and SpO₂ are captured in the Vitals step.</p>
              <div className="form-group">
                <label>Breathing</label>
                <div className="clog-chip-row">{COPD_BREATHING.map(b => <Chip key={b} selected={data.copd_breathing === b} onClick={() => update({ copd_breathing: b })}>{b}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Cough</label>
                <div className="clog-chip-row">{COPD_COUGH.map(c => <Chip key={c} selected={data.copd_cough === c} onClick={() => update({ copd_cough: c })}>{c}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Sputum change</label>
                <div className="clog-chip-row">{['None','Yes'].map(v => <Chip key={v} selected={data.copd_sputum_change === v} onClick={() => update({ copd_sputum_change: v })}>{v}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Oxygen</label>
                <div className="clog-chip-row">{COPD_OXYGEN_USED.map(o => <Chip key={o} selected={data.copd_oxygen_used === o} onClick={() => update({ copd_oxygen_used: o })}>{o}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'cardiac' && (
            <div data-testid="clog-module-cardiac">
              <h2 className="clog-step-title">Heart Failure / Cardiac Module</h2>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '-0.5rem' }}>Weight, BP, pulse, and SpO₂ are captured in the Vitals step.</p>
              <div className="form-group">
                <label>Swelling — location</label>
                <div className="clog-chip-row">{SWELLING_LOCATIONS.map(s => <Chip key={s} selected={data.swelling_locations.includes(s)} onClick={() => toggle('swelling_locations', s)}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Swelling severity</label>
                <div className="clog-chip-row">{SWELLING_SEVERITY.map(s => <Chip key={s} selected={data.swelling_severity === s} onClick={() => update({ swelling_severity: s })}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Breathing</label>
                <div className="clog-chip-row">{CARDIAC_BREATHING.map(b => <Chip key={b} selected={data.cardiac_breathing === b} onClick={() => update({ cardiac_breathing: b })}>{b}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Fatigue</label>
                <div className="clog-chip-row">{CARDIAC_FATIGUE.map(f => <Chip key={f} selected={data.cardiac_fatigue === f} onClick={() => update({ cardiac_fatigue: f })}>{f}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'parkinsons' && (
            <div data-testid="clog-module-parkinsons">
              <h2 className="clog-step-title">Parkinson's Module</h2>
              <div className="form-group">
                <label>Tremor</label>
                <div className="clog-chip-row">{TREMOR_SEVERITY.map(t => <Chip key={t} selected={data.tremor_severity === t} onClick={() => update({ tremor_severity: t })}>{t}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Movement</label>
                <div className="clog-chip-row">{PARK_MOVEMENT.map(m => <Chip key={m} selected={data.parkinsons_movement === m} onClick={() => update({ parkinsons_movement: m })}>{m}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Balance</label>
                <div className="clog-chip-row">{PARK_BALANCE.map(b => <Chip key={b} selected={data.parkinsons_balance === b} onClick={() => update({ parkinsons_balance: b })}>{b}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Swallowing</label>
                <div className="clog-chip-row">{PARK_SWALLOWING.map(s => <Chip key={s} selected={data.parkinsons_swallowing === s} onClick={() => update({ parkinsons_swallowing: s })}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Speech</label>
                <div className="clog-chip-row">{PARK_SPEECH.map(s => <Chip key={s} selected={data.parkinsons_speech === s} onClick={() => update({ parkinsons_speech: s })}>{s}</Chip>)}</div>
              </div>
            </div>
          )}

          {currentKey === 'stroke' && (
            <div data-testid="clog-module-stroke">
              <h2 className="clog-step-title">Stroke Recovery Module</h2>
              <div className="clog-grid-2">
                <div className="form-group"><label>Left arm strength</label><div className="clog-chip-row">{STRENGTH_LEVELS.map(v => <Chip key={v} selected={data.strength_la === v} onClick={() => update({ strength_la: v })}>{v}</Chip>)}</div></div>
                <div className="form-group"><label>Right arm strength</label><div className="clog-chip-row">{STRENGTH_LEVELS.map(v => <Chip key={v} selected={data.strength_ra === v} onClick={() => update({ strength_ra: v })}>{v}</Chip>)}</div></div>
                <div className="form-group"><label>Left leg strength</label><div className="clog-chip-row">{STRENGTH_LEVELS.map(v => <Chip key={v} selected={data.strength_ll === v} onClick={() => update({ strength_ll: v })}>{v}</Chip>)}</div></div>
                <div className="form-group"><label>Right leg strength</label><div className="clog-chip-row">{STRENGTH_LEVELS.map(v => <Chip key={v} selected={data.strength_rl === v} onClick={() => update({ strength_rl: v })}>{v}</Chip>)}</div></div>
              </div>
              <div className="form-group">
                <label>Speech</label>
                <div className="clog-chip-row">{STROKE_SPEECH.map(s => <Chip key={s} selected={data.stroke_speech === s} onClick={() => update({ stroke_speech: s })}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Swallowing</label>
                <div className="clog-chip-row">{STROKE_SWALLOWING.map(s => <Chip key={s} selected={data.stroke_swallowing === s} onClick={() => update({ stroke_swallowing: s })}>{s}</Chip>)}</div>
              </div>
              <div className="form-group">
                <label>Therapy exercises</label>
                <div className="clog-chip-row">{THERAPY_STATUS.map(t => <Chip key={t} selected={data.therapy_status === t} onClick={() => update({ therapy_status: t })}>{t}</Chip>)}</div>
              </div>
            </div>
          )}

          {/* STEP — REVIEW */}
          {currentKey === 'review' && (
            <div data-testid="clog-review">
              <h2 className="clog-step-title">Review & Sign</h2>
              <div className="clog-review-grid">
                {Object.entries(completeness()).map(([label, done]) => (
                  <div key={label} className={`clog-review-item ${done ? 'done' : 'pending'}`} data-testid={`clog-review-${label.toLowerCase()}`}>
                    <i className={`fa-solid ${done ? 'fa-check' : 'fa-hourglass-half'}`} /> {label}
                    <span>{done ? 'Recorded' : 'Not started'}</span>
                  </div>
                ))}
              </div>
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Caregiver signature (type your full name)</label>
                <input type="text" value={data.signed_by} onChange={e => update({ signed_by: e.target.value })} placeholder="Your full name" data-testid="clog-signature" />
              </div>
              <div className="clog-attest">
                <i className="fa-solid fa-shield-halved" />
                <span>By submitting this log, I confirm that this information reflects my observations and care documentation for this visit.</span>
              </div>
              <button
                className="submit-button clog-submit"
                onClick={submitLog}
                disabled={saving || !data.signed_by}
                data-testid="clog-submit-btn"
              >
                {saving ? 'Submitting…' : <><i className="fa-solid fa-signature" /> Sign & Submit Log</>}
              </button>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="clog-footer">
          <button onClick={prev} disabled={step === 0} className="clog-nav-btn" data-testid="clog-prev-btn">
            <i className="fa-solid fa-arrow-left" /> Previous
          </button>
          <button onClick={saveDraft} disabled={saving} className="clog-nav-btn secondary" data-testid="clog-save-draft-btn">
            <i className="fa-solid fa-floppy-disk" /> {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button onClick={next} disabled={step === visibleSteps.length - 1} className="clog-nav-btn primary" data-testid="clog-next-btn">
            Next <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareLogWizard;
